import {z} from 'zod';

import {
  BaseVoidRequestSchema,
  BaseVoidResponseSchema,
  CreatedAtAndUpdatedBySchema,
} from '@dms/api/shared';

const BaseDirectSaleTypeSchema = z.object({
  directSaleTypeId: z.string(),
  name: z.string(),
});
export type BaseDirectSaleType = z.infer<typeof BaseDirectSaleTypeSchema>;

const BaseDirectSaleVariantSchema = z.object({
  directSaleVariantId: z.string(),
  name: z.string(),
});
export type BaseDirectSaleVariant = z.infer<typeof BaseDirectSaleVariantSchema>;

const DefaultPaymentTypeSchema = z.enum(['CASH', 'CARD', 'BANK_TRANSFER', 'INTERNAL']);
export type DefaultPaymentType = z.infer<typeof DefaultPaymentTypeSchema>;

const DocumentSeriesSchema = z.object({
  docSeriesId: z.string().nullish(),
});

const TemplateAndDocSeriesSchema = z.object({
  templateId: z.string().nullish(),
  docSeriesId: z.string().nullish(),
});

const FullPaymentDocumentsSchema = z.object({
  invoice: TemplateAndDocSeriesSchema.nullish(),
  deposit: DocumentSeriesSchema.nullish(),
  invoiceCorrection: DocumentSeriesSchema.nullish(),
  taxDocument: DocumentSeriesSchema.nullish(),
  taxDocumentCorrection: DocumentSeriesSchema.nullish(),
  invoiceEu: DocumentSeriesSchema.nullish(),
  invoiceEuCorrection: DocumentSeriesSchema.nullish(),
  invoiceOutsideEu: DocumentSeriesSchema.nullish(),
  invoiceOutsideEuCorrection: DocumentSeriesSchema.nullish(),
});

const InternalPaymentDocumentsSchema = z.object({
  invoice: TemplateAndDocSeriesSchema.nullish(),
  invoiceCorrection: DocumentSeriesSchema.nullish(),
});

const BasePaymentSchema = z.object({
  isEnable: z.boolean(),
});

const FullPaymentSchema = BasePaymentSchema.merge(FullPaymentDocumentsSchema);
const InternalPaymentSchema = BasePaymentSchema.merge(InternalPaymentDocumentsSchema);

const FastTrackIssueSchema = z.object({
  isEnable: z.boolean(),
  templateId: z.string().nullish(),
  docSeriesId: z.string().nullish(),
});

const WarehousesPrioritySchema = z.array(
  z.object({
    warehouseId: z.string(),
    warehouseName: z.string(),
    warehouseNumber: z.string(),
    warehousePriority: z.number(),
    authorizationProfile: z.object({
      id: z.string(),
      name: z.string(),
    }),
    branch: z.object({
      id: z.string(),
      name: z.string(),
    }),
  })
);

const WarehousePrioritySchema = z.object({
  warehousePriority: z.object({warehouseIds: z.array(z.string())}),
});

const DirectSaleVariantDetailSchema = z.object({
  general: z.object({
    directSaleVariantId: z.string().nullish(),
    directSaleTypeId: z.string(),
    name: z.string(),
    priceType: z.string(),
    rateType: z.string(),
    priceTypeRatio: z.number(),
    rateTypeRatio: z.number(),
    minimalMaterialMargin: z.number().optional(),
    minimalWorkMargin: z.number().optional(),
    defaultCustomerId: z.string().nullish(),
    sufix: z.string(),
    authorizationProfileId: z.string(),
    docSeriesId: z.string(),
    isUnitPriceWithVat: z.boolean(),
    note: z.string().optional(),
    isChangeableDefaultCustomer: z.boolean(),
  }),
  documents: z.object({
    defaultPaymentType: DefaultPaymentTypeSchema.nullish(),
    orderOffer: z.object({
      templateId: z.string().nullish(),
      docSeriesId: z.string().nullish(),
    }),
    fastTrackIssue: FastTrackIssueSchema,
    cashPayment: FullPaymentSchema,
    cardPayment: FullPaymentSchema,
    bankPayment: FullPaymentSchema,
    internalPayment: InternalPaymentSchema,
  }),
  accounting: z
    .object({
      directSaleVariantId: z.string().nullish(),
      isTaxDeductible: z.boolean().nullish(),
      fixedVatType: z.string().nullish(),
      excludedServiceCenterIds: z.array(z.string()).nullish(),
      defaultServiceCenterId: z.string().nullish(),
      isDefaultServiceCenterLocked: z.boolean().nullish(),
      defaultLabourWorkItemGroupId: z.string().nullish(),
      isDefaultLabourWorkItemGroupLocked: z.boolean().nullish(),
      defaultMaterialWorkItemGroupId: z.string().nullish(),
      isDefaultMaterialWorkItemGroupLocked: z.boolean().nullish(),
    })
    .merge(CreatedAtAndUpdatedBySchema),
  reporting: z
    .object({
      directSaleVariantId: z.string().nullish(),
      excludedServiceOrderGroupIds: z.array(z.string()).nullish(),
      defaultServiceOrderGroupId: z.string().nullish(),
      isDefaultServiceOrderGroupLocked: z.boolean().nullish(),
      isPreDeliveryInspection: z.boolean().nullish(),
    })
    .merge(CreatedAtAndUpdatedBySchema),
});
export type DirectSaleVariantDetail = z.infer<typeof DirectSaleVariantDetailSchema>;

/**
 * GET requests
 */
export const GetDirectSaleTypesRequestSchema = BaseVoidRequestSchema;
export type GetDirectSaleTypesRequest = z.infer<typeof GetDirectSaleTypesRequestSchema>;

export const GetDirectSaleTypesResponseSchema = z.array(BaseDirectSaleTypeSchema);
export type GetDirectSaleTypesResponse = z.infer<typeof GetDirectSaleTypesResponseSchema>;

export const GetDirectSaleVariantsRequestSchema = BaseVoidRequestSchema;
export type GetDirectSaleVariantsRequest = z.infer<typeof GetDirectSaleVariantsRequestSchema>;

export const GetDirectSaleVariantsResponseSchema = z.array(BaseDirectSaleVariantSchema);
export type GetDirectSaleVariantsResponse = z.infer<typeof GetDirectSaleVariantsResponseSchema>;

export const GetDirectSaleVariantRequestSchema = z.object({
  directSaleVariantId: z.string(),
});
export type GetDirectSaleVariantRequest = z.infer<typeof GetDirectSaleVariantRequestSchema>;

export const GetDirectSaleVariantResponseSchema = DirectSaleVariantDetailSchema.merge(
  CreatedAtAndUpdatedBySchema
).merge(
  z.object({
    warehousesPriority: WarehousesPrioritySchema,
  })
);
export type GetDirectSaleVariantResponse = z.infer<typeof GetDirectSaleVariantResponseSchema>;

export const GetDirectSaleVariantDefaultVarehousesPriorityRequestSchema = BaseVoidRequestSchema;
export type GetDirectSaleVariantDefaultVarehousesPriorityRequest = z.infer<
  typeof GetDirectSaleVariantDefaultVarehousesPriorityRequestSchema
>;

export const GetDirectSaleVariantDefaultVarehousesPriorityResponseSchema = z.array(
  z.object({
    warehouseId: z.string(),
    warehouseName: z.string(),
    warehouseNumber: z.string(),
    warehousePriority: z.number(),
    authorizationProfile: z.object({
      id: z.string(),
      name: z.string(),
    }),
    branch: z.object({
      id: z.string(),
      name: z.string(),
    }),
  })
);
export type GetDirectSaleVariantDefaultVarehousesPriorityResponse = z.infer<
  typeof GetDirectSaleVariantDefaultVarehousesPriorityResponseSchema
>;

/**
 * POST requests
 */
export const PostDirectSaleVariantRequestSchema = DirectSaleVariantDetailSchema.omit({
  general: true,
  accounting: true,
  reporting: true,
})
  .merge(
    z.object({
      general: DirectSaleVariantDetailSchema.shape.general.omit({directSaleVariantId: true}),
      accounting: z.object({
        isTaxDeductible: z.boolean(),
        fixedVatType: z.string().nullish(),
        excludedServiceCenterIds: z.array(z.string()),
        defaultServiceCenterId: z.string(),
        isDefaultServiceCenterLocked: z.boolean(),
        defaultLabourWorkItemGroupId: z.string(),
        isDefaultLabourWorkItemGroupLocked: z.boolean(),
        defaultMaterialWorkItemGroupId: z.string(),
        isDefaultMaterialWorkItemGroupLocked: z.boolean(),
      }),
      reporting: z.object({
        excludedServiceOrderGroupIds: z.array(z.string()),
        defaultServiceOrderGroupId: z.string(),
        isDefaultServiceOrderGroupLocked: z.boolean(),
        isPreDeliveryInspection: z.boolean(),
      }),
    })
  )
  .merge(WarehousePrioritySchema);
export type PostDirectSaleVariantRequest = z.infer<typeof PostDirectSaleVariantRequestSchema>;

export const PostDirectSaleVariantResponseSchema = z.object({directSaleVariantId: z.string()});
export type PostDirectSaleVariantResponse = z.infer<typeof PostDirectSaleVariantResponseSchema>;

/**
 * PATCH requests
 */
export const PatchDirectSaleVariantRequestSchema = z.object({
  directSaleVariantId: z.string(),
  body: DirectSaleVariantDetailSchema.merge(WarehousePrioritySchema),
});
export type PatchDirectSaleVariantRequest = z.infer<typeof PatchDirectSaleVariantRequestSchema>;

export const PatchDirectSaleVariantResponseSchema = BaseVoidResponseSchema;
export type PatchDirectSaleVariantResponse = z.infer<typeof PatchDirectSaleVariantResponseSchema>;

/**
 * DELETE requests
 */
export const BulkDeleteDirectSaleVariantRequestSchema = z.object({
  directSaleVariantId: z.array(z.string()),
});
export type BulkDeleteDirectSaleVariantRequest = z.infer<
  typeof BulkDeleteDirectSaleVariantRequestSchema
>;

export const BulkDeleteDirectSaleVariantResponseSchema = BaseVoidResponseSchema;
export type BulkDeleteDirectSaleVariantResponse = z.infer<
  typeof BulkDeleteDirectSaleVariantResponseSchema
>;
